import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Lenis from "lenis";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ArrowRight,
  ArrowUpRight,
  Star,
  CheckCircle2,
  ShieldCheck,
  Clock,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BRAND,
  IMAGES,
  SERVICES,
  SERVICE_OPTIONS,
  STATS,
  PROJECTS,
  HOME_BUILD,
  GALLERY,
  MANIFESTO,
  MARQUEE,
} from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#E0531C";
const INK = "#17130F";

const NAV = [
  { label: "Work", href: "#projects" },
  { label: "Services", href: "#services" },
  { label: "Approach", href: "#approach" },
];

/* ---------- Motion helpers ---------- */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

function MaskLine({ children, className = "" }) {
  return (
    <span className="line-mask">
      <motion.span variants={lineChild} className={`block ${className}`}>
        {children}
      </motion.span>
    </span>
  );
}

/* Image with hover zoom — always visible (no fragile scroll-clip that can leave it hidden) */
function RevealImage({ src, alt, className = "", imgClass = "" }) {
  return (
    <div className={`overflow-hidden ${className}`}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full h-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-105 ${imgClass}`}
      />
    </div>
  );
}

/* ---------- Smooth scroll ---------- */
function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 0.9 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (id.length < 2) return;
      const el = document.querySelector(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -80, duration: 1.4 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);
}

/* ---------- Header ---------- */
function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F5F1EA]/85 backdrop-blur-xl border-b border-black/5 py-0"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src={BRAND.logoLight}
            alt="Kairos Buildings"
            className="h-11 w-11 object-contain rounded-md"
          />
          <span
            className={`font-display font-semibold text-lg tracking-tight transition-colors ${
              scrolled ? "text-[#17130F]" : "text-white"
            }`}
          >
            Kairos Buildings
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-[13px] uppercase tracking-[0.18em] font-medium transition-colors relative group ${
                scrolled ? "text-[#17130F]/70 hover:text-[#17130F]" : "text-white/80 hover:text-white"
              }`}
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#E0531C] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <a href="#quote">
            <Button
              data-testid="header-quote-button"
              className="bg-[#E0531C] hover:bg-[#c4440f] text-white rounded-none px-6 h-11 uppercase tracking-[0.14em] text-xs font-semibold"
            >
              Request a Quote
            </Button>
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className={`md:hidden ${scrolled ? "text-[#17130F]" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-[#F5F1EA] border-t border-black/5 overflow-hidden"
          >
            <div className="px-6 py-5 space-y-3">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="block text-[#17130F] font-medium py-1 uppercase tracking-[0.16em] text-sm"
                >
                  {n.label}
                </a>
              ))}
              <a href="#quote" onClick={() => setOpen(false)}>
                <Button className="w-full bg-[#E0531C] hover:bg-[#c4440f] text-white rounded-none mt-2">
                  Request a Quote
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden grain"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={IMAGES.hero}
          alt="Custom home built by Kairos Buildings in the West Kootenays"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-[#100c08] via-[#100c08]/40 to-[#100c08]/20"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#100c08]/60 to-transparent" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full pb-16 lg:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="h-px w-10 bg-[#E0531C]" />
          <span className="text-[#E0531C] text-xs uppercase tracking-[0.32em] font-semibold">
            Residential General Contractor · West Kootenays
          </span>
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="font-display text-white font-medium tracking-[-0.02em] leading-[0.94] text-[15vw] sm:text-[12vw] lg:text-[9.5rem]"
        >
          <MaskLine>Create &amp; build</MaskLine>
          <MaskLine className="italic text-[#E0531C]">together.</MaskLine>
        </motion.h1>

        <div className="mt-9 grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-16 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="max-w-xl text-base sm:text-lg text-white/80 leading-relaxed"
          >
            Every project begins with vision. Every vision needs a plan. From the
            first sketch to the final walkthrough, we bring your project to life —
            with educated planning and craft held in mind for every step.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="flex flex-col sm:flex-row lg:flex-col gap-4"
          >
            <a href="#quote">
              <Button
                data-testid="hero-quote-button"
                className="group bg-[#E0531C] hover:bg-[#c4440f] text-white rounded-none px-8 h-14 text-sm uppercase tracking-[0.14em] font-semibold w-full sm:w-auto lg:w-full"
              >
                Let's begin
                <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={17} />
              </Button>
            </a>
            <a href="#projects">
              <Button
                data-testid="hero-projects-button"
                variant="outline"
                className="rounded-none px-8 h-14 text-sm uppercase tracking-[0.14em] font-semibold bg-transparent border-white/40 text-white hover:bg-white hover:text-[#17130F] w-full sm:w-auto lg:w-full"
              >
                View our work
              </Button>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Editorial marquee ---------- */
function Marquee() {
  const row = [...MARQUEE, ...MARQUEE];
  return (
    <section className="bg-[#17130F] py-6 overflow-hidden select-none" aria-hidden="true">
      <div className="flex w-max animate-marquee whitespace-nowrap">
        {row.map((t, i) => (
          <span key={i} className="flex items-center">
            <span className="font-display italic text-[#F5F1EA] text-2xl sm:text-3xl px-8">
              {t}
            </span>
            <span className="text-[#E0531C] text-2xl">✳</span>
          </span>
        ))}
      </div>
    </section>
  );
}

/* ---------- Manifesto (numbered chapters) ---------- */
function Manifesto() {
  return (
    <section id="approach" data-testid="approach-section" className="bg-[#F5F1EA] py-24 lg:py-36">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-24">
        <div className="lg:sticky lg:top-28 h-max">
          <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#E0531C]">
            The Kairos Way
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#17130F] mt-5 leading-[1.02] tracking-[-0.02em]">
            How a home <span className="italic text-[#E0531C]">takes shape</span>
          </h2>
          <p className="text-[#17130F]/60 mt-6 text-lg leading-relaxed max-w-md">
            Four beliefs guide every build we take on — from a single staircase to
            a ground-up custom home.
          </p>
        </div>

        <div>
          {MANIFESTO.map((c, i) => (
            <motion.div
              key={c.no}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              data-testid={`manifesto-${c.no}`}
              className="group grid grid-cols-[auto_1fr] gap-6 sm:gap-10 py-9 border-t border-[#17130F]/15 first:border-t-0"
            >
              <span className="font-display text-4xl sm:text-5xl font-medium text-[#17130F]/25 group-hover:text-[#E0531C] transition-colors duration-500">
                {c.no}
              </span>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl font-medium text-[#17130F] tracking-[-0.01em]">
                  {c.title}
                </h3>
                <p className="text-[#17130F]/60 mt-3 text-base sm:text-lg leading-relaxed max-w-xl">
                  {c.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Services ---------- */
function Services() {
  return (
    <section id="services" data-testid="services-section" className="bg-[#17130F] py-24 lg:py-36 text-[#F5F1EA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#E0531C]">
              What We Do
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium mt-5 leading-[1.02] tracking-[-0.02em]">
              Full-service residential construction
            </h2>
          </div>
          <p className="text-[#F5F1EA]/55 max-w-sm text-lg leading-relaxed">
            From the first sketch to the final handshake, one accountable team
            delivers every detail.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-white/10">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-50px" }}
                data-testid={`service-card-${i}`}
                className="group relative p-8 lg:p-10 border-b border-r border-white/10 hover:bg-white/[0.03] transition-colors duration-500"
              >
                <div className="flex items-start justify-between">
                  <Icon className="text-[#E0531C]" size={30} strokeWidth={1.5} />
                  <span className="font-display text-lg text-white/20">0{i + 1}</span>
                </div>
                <h3 className="font-display text-2xl font-medium mt-10">{s.title}</h3>
                <p className="text-[#F5F1EA]/55 mt-3 leading-relaxed">{s.desc}</p>
                <ArrowUpRight
                  className="mt-6 text-white/25 group-hover:text-[#E0531C] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
                  size={22}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Build progression ---------- */
function BuildProgression() {
  return (
    <div className="mb-28 lg:mb-40">
      <div className="grid lg:grid-cols-[1fr_1fr] gap-8 lg:gap-16 items-end mb-12">
        <div>
          <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#E0531C]">
            {HOME_BUILD.eyebrow}
          </span>
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium text-[#17130F] mt-4 leading-[1.03] tracking-[-0.02em]">
            {HOME_BUILD.title}
          </h3>
        </div>
        <p className="text-[#17130F]/60 text-lg leading-relaxed">{HOME_BUILD.description}</p>
      </div>

      <div className="flex gap-4 lg:gap-5 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 snap-x lg:grid lg:grid-cols-5 -mx-6 px-6 lg:mx-0 lg:px-0">
        {HOME_BUILD.steps.map((s, i) => (
          <motion.div
            key={s.no}
            variants={fadeUp}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-40px" }}
            data-testid={`build-step-${s.no}`}
            className={`group shrink-0 w-[70vw] sm:w-[45vw] lg:w-auto snap-start ${
              i % 2 === 1 ? "lg:mt-10" : ""
            }`}
          >
            <div className="relative overflow-hidden aspect-[3/4]">
              <img
                src={s.src}
                alt={`Custom home construction in the West Kootenays — ${s.label}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <span className="absolute top-3 left-3 font-display text-white text-lg drop-shadow-lg">
                {s.no}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="h-px w-4 bg-[#E0531C]" />
              <span className="text-[13px] uppercase tracking-[0.12em] text-[#17130F]/70 font-medium">
                {s.label}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Before / After story ---------- */
function ProjectStory({ project, index }) {
  const flip = index % 2 === 1;
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      data-testid={`project-story-${project.id}`}
      className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center"
    >
      <div className={`grid grid-cols-2 gap-3 sm:gap-4 ${flip ? "lg:order-2" : ""}`}>
        {[
          { src: project.before, label: project.beforeLabel, tone: "bg-[#17130F]" },
          { src: project.after, label: project.afterLabel, tone: "bg-[#E0531C]" },
        ].map((img, i) => (
          <div key={i} className="relative group">
            <RevealImage
              src={img.src}
              alt={`${project.title} — ${img.label}`}
              className="aspect-[3/4]"
            />
            <span
              className={`absolute top-3 left-3 ${img.tone} text-white text-[11px] font-semibold uppercase tracking-[0.16em] px-3 py-1.5`}
            >
              {img.label}
            </span>
          </div>
        ))}
      </div>

      <div className={flip ? "lg:order-1" : ""}>
        <div className="flex items-center gap-3">
          <span className="h-px w-8 bg-[#E0531C]" />
          <span className="text-xs uppercase tracking-[0.24em] font-semibold text-[#E0531C]">
            {project.category}
          </span>
        </div>
        <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium text-[#17130F] mt-5 leading-[1.03] tracking-[-0.02em]">
          {project.title}
        </h3>
        <p className="text-[#17130F]/60 mt-5 text-lg leading-relaxed">{project.description}</p>
        <div className="mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#17130F]">
          <span className="h-2 w-2 rounded-full bg-[#E0531C]" /> Before &amp; after — real Kairos work
        </div>
      </div>
    </motion.div>
  );
}

/* ---------- Projects section ---------- */
function Projects() {
  return (
    <section id="projects" data-testid="projects-section" className="py-24 lg:py-36 bg-[#F5F1EA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 lg:mb-24">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#E0531C]">
              Selected Work
            </span>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium text-[#17130F] mt-5 leading-[0.98] tracking-[-0.02em]">
              Projects we are <span className="italic text-[#E0531C]">proud of</span>
            </h2>
          </div>
          <a href="#quote">
            <Button
              variant="outline"
              className="rounded-none border-[#17130F]/25 text-[#17130F] hover:bg-[#17130F] hover:text-[#F5F1EA] px-6 h-12 uppercase tracking-[0.14em] text-xs font-semibold"
              data-testid="projects-cta-button"
            >
              Start your project <ArrowRight className="ml-2" size={15} />
            </Button>
          </a>
        </div>

        <BuildProgression />

        <div className="space-y-24 lg:space-y-40">
          {PROJECTS.map((p, i) => (
            <ProjectStory key={p.id} project={p} index={i} />
          ))}
        </div>

        {/* Masonry gallery */}
        <div className="mt-28 lg:mt-40 pt-14 border-t border-[#17130F]/10">
          <div className="flex items-end justify-between mb-10">
            <h3 className="font-display text-3xl sm:text-4xl font-medium text-[#17130F] tracking-[-0.01em]">
              More of our work
            </h3>
            <span className="text-xs uppercase tracking-[0.22em] text-[#17130F]/40 font-medium hidden sm:block">
              West Kootenays · Trail → Nelson
            </span>
          </div>
          <div className="columns-2 lg:columns-4 gap-4 lg:gap-5 [column-fill:_balance]">
            {GALLERY.map((g, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                data-testid={`gallery-item-${i}`}
                className="relative group mb-4 lg:mb-5 break-inside-avoid overflow-hidden"
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${
                    g.tall ? "aspect-[3/4]" : "aspect-[4/3]"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#100c08]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 right-4 text-white font-medium text-sm translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  {g.caption}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- About + Stats ---------- */
function About() {
  return (
    <section id="about" data-testid="about-section" className="py-24 lg:py-36 bg-[#17130F] text-[#F5F1EA] grain relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="relative"
          >
            <RevealImage
              src="/projects/home-5-finished.jpg"
              alt="A finished Kairos custom home"
              className="w-full h-[500px]"
            />
            <div className="absolute -bottom-6 -right-3 sm:right-6 bg-[#E0531C] px-7 py-5">
              <div className="font-display text-4xl font-medium">150+</div>
              <div className="text-sm text-white/85 uppercase tracking-[0.14em]">Projects Delivered</div>
            </div>
          </motion.div>

          <div>
            <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#E0531C]">
              Why Kairos
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium mt-5 leading-[1.02] tracking-[-0.02em]">
              Built on trust. <span className="italic text-[#E0531C]">Delivered with craft.</span>
            </h2>
            <p className="text-[#F5F1EA]/60 mt-6 text-lg leading-relaxed">
              We're a residential general contractor that treats your project like
              our own. One dedicated team, transparent pricing, and obsessive
              attention to detail — that's how we've earned referrals for nearly
              two decades.
            </p>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-9">
              {[
                "Fixed, transparent estimates",
                "Dedicated project manager",
                "Premium materials & trades",
                "Clear weekly progress updates",
                "Workmanship warranty",
                "Fully licensed & insured",
              ].map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <CheckCircle2 className="text-[#E0531C] shrink-0" size={19} />
                  <span className="text-[#F5F1EA]/85">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-20 lg:mt-28 border-t border-white/10 pt-12">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              data-testid={`stat-${i}`}
            >
              <div className="font-display text-5xl lg:text-6xl font-medium text-[#F5F1EA]">{s.value}</div>
              <div className="text-[#F5F1EA]/50 text-sm mt-2 uppercase tracking-[0.14em]">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Testimonials ---------- */
function Testimonials() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 lg:py-36 bg-[#F5F1EA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#E0531C]">
            Homeowner Stories
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#17130F] mt-5 leading-[1.02] tracking-[-0.02em]">
            Loved by the families we build for
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {items.map((t, i) => (
            <motion.div
              key={t.id}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              data-testid={`testimonial-${i}`}
              className="bg-white border border-[#17130F]/8 p-8 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
            >
              <div className="flex gap-1 text-[#E0531C] mb-5">
                {[...Array(t.rating || 5)].map((_, s) => (
                  <Star key={s} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="font-display text-lg text-[#17130F]/85 leading-relaxed flex-1 italic">
                "{t.quote}"
              </p>
              <div className="mt-7 pt-5 border-t border-[#17130F]/10">
                <div className="font-semibold text-[#17130F]">{t.name}</div>
                <div className="text-sm text-[#17130F]/50">{t.project}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Quote form ---------- */
const EMPTY = { name: "", email: "", phone: "", service_type: "", message: "", company_website: "" };

function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service_type || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Thanks! We'll be in touch within one business day.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-24 lg:py-36 bg-[#17130F] text-[#F5F1EA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#E0531C]">
            Get Started
          </span>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium mt-5 leading-[0.98] tracking-[-0.02em]">
            Request your <span className="italic text-[#E0531C]">free quote</span>
          </h2>
          <p className="text-[#F5F1EA]/60 mt-6 text-lg leading-relaxed max-w-md">
            Tell us about your project and we'll get back to you within one
            business day with next steps and a no-obligation estimate.
          </p>

          <div className="mt-12 space-y-6">
            <a href={`tel:${BRAND.phone}`} className="flex items-center gap-4 group" data-testid="contact-phone">
              <div className="w-12 h-12 bg-[#E0531C] flex items-center justify-center shrink-0">
                <Phone className="text-white" size={19} />
              </div>
              <div>
                <div className="text-xs text-[#F5F1EA]/50 uppercase tracking-[0.14em]">Call us</div>
                <div className="font-semibold group-hover:text-[#E0531C] transition-colors">{BRAND.phone}</div>
              </div>
            </a>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
              <div className="w-12 h-12 bg-[#E0531C] flex items-center justify-center shrink-0">
                <Mail className="text-white" size={19} />
              </div>
              <div>
                <div className="text-xs text-[#F5F1EA]/50 uppercase tracking-[0.14em]">Email us</div>
                <div className="font-semibold group-hover:text-[#E0531C] transition-colors">{BRAND.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#E0531C] flex items-center justify-center shrink-0">
                <MapPin className="text-white" size={19} />
              </div>
              <div>
                <div className="text-xs text-[#F5F1EA]/50 uppercase tracking-[0.14em]">Service Area</div>
                <div className="font-semibold">{BRAND.address}</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="quote-form"
          className="bg-[#F5F1EA] text-[#17130F] p-8 lg:p-10"
        >
          {/* Honeypot — hidden from humans, bots tend to fill it */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
            <label htmlFor="company_website">Company website (leave blank)</label>
            <input
              id="company_website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.company_website}
              onChange={(e) => set("company_website", e.target.value)}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="uppercase text-xs tracking-[0.14em] text-[#17130F]/60">Full name *</Label>
              <Input
                id="name"
                data-testid="quote-name-input"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Jane Doe"
                className="rounded-none border-[#17130F]/20 bg-white focus-visible:ring-[#E0531C]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="uppercase text-xs tracking-[0.14em] text-[#17130F]/60">Phone</Label>
              <Input
                id="phone"
                data-testid="quote-phone-input"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="(555) 000-0000"
                className="rounded-none border-[#17130F]/20 bg-white focus-visible:ring-[#E0531C]"
              />
            </div>
          </div>

          <div className="space-y-2 mt-5">
            <Label htmlFor="email" className="uppercase text-xs tracking-[0.14em] text-[#17130F]/60">Email *</Label>
            <Input
              id="email"
              type="email"
              data-testid="quote-email-input"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="jane@email.com"
              className="rounded-none border-[#17130F]/20 bg-white focus-visible:ring-[#E0531C]"
            />
          </div>

          <div className="space-y-2 mt-5">
            <Label className="uppercase text-xs tracking-[0.14em] text-[#17130F]/60">Service needed *</Label>
            <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
              <SelectTrigger data-testid="quote-service-select" className="rounded-none border-[#17130F]/20 bg-white">
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o} data-testid={`service-option-${o}`}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 mt-5">
            <Label htmlFor="message" className="uppercase text-xs tracking-[0.14em] text-[#17130F]/60">Project details *</Label>
            <Textarea
              id="message"
              data-testid="quote-message-input"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Tell us about your project, timeline and budget…"
              rows={5}
              className="rounded-none border-[#17130F]/20 bg-white focus-visible:ring-[#E0531C]"
            />
          </div>

          <Button
            type="submit"
            data-testid="quote-submit-button"
            disabled={loading}
            className="w-full mt-7 bg-[#E0531C] hover:bg-[#c4440f] text-white rounded-none py-6 text-sm uppercase tracking-[0.16em] font-semibold"
          >
            {loading ? "Sending…" : "Send My Request"}
          </Button>
        </form>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#100c08] text-[#F5F1EA]/60 pt-20 pb-8">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-4 gap-10 pb-14 border-b border-white/10">
          <div className="md:col-span-2">
            <img src={BRAND.logoDark} alt="Kairos Buildings" className="h-16 w-16 object-contain" />
            <p className="font-display italic text-2xl text-[#F5F1EA] mt-5 max-w-sm leading-snug">
              {BRAND.tagline}.
            </p>
            <p className="mt-4 max-w-sm leading-relaxed text-sm">
              Residential general contracting, custom homes and remodeling crafted to last.
            </p>
          </div>
          <div>
            <h4 className="text-[#F5F1EA] font-semibold mb-5 uppercase tracking-[0.16em] text-xs">Explore</h4>
            <ul className="space-y-3 text-sm">
              {NAV.map((n) => (
                <li key={n.href}>
                  <a href={n.href} className="hover:text-[#E0531C] transition-colors">{n.label}</a>
                </li>
              ))}
              <li>
                <a href="#quote" className="hover:text-[#E0531C] transition-colors">Request a Quote</a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-[#F5F1EA] font-semibold mb-5 uppercase tracking-[0.16em] text-xs">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2"><Phone size={15} /> {BRAND.phone}</li>
              <li className="flex items-center gap-2"><Mail size={15} /> {BRAND.email}</li>
              <li className="flex items-start gap-2"><MapPin size={15} className="mt-1 shrink-0" /> {BRAND.address}</li>
            </ul>
          </div>
        </div>
        <div className="pt-6 text-xs flex flex-col sm:flex-row justify-between gap-3 uppercase tracking-[0.12em]">
          <span>© {new Date().getFullYear()} Kairos Buildings. All rights reserved.</span>
          <div className="flex items-center gap-5">
            <span>Licensed &amp; Insured</span>
            <a href="/login" data-testid="footer-admin-link" className="hover:text-[#E0531C] transition-colors">
              Admin
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  useLenis();
  return (
    <div className="bg-[#F5F1EA]">
      <Header />
      <Hero />
      <Marquee />
      <Manifesto />
      <Services />
      <Projects />
      <About />
      <QuoteForm />
      <Footer />
    </div>
  );
}
